<script setup lang="ts">
import { computed, inject, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSteps } from '/@src/composable/useSteps'
import { NazvyKrokov } from '/@src/utils/zoppm/types'
import { formatDateString } from '/@src/utils/format'
import { ZoppmSchemeTransformer } from '/@src/utils/zoppm'
import { NazvyAssemblySekcii } from '/@src/utils/zoppm/ZoppmSchemeTransformer/types'

export interface UdajeOProjekte {
  aktivity: any
  isValidSection: boolean
}

const props = withDefaults(defineProps<UdajeOProjekte>(), {
  aktivity: undefined,
  isValidSection: false,
})

const { t } = useI18n()
const steps = useSteps()

const stavPodana = inject<boolean>('stavPodana', false)

const aktivityData = computed(() => props?.aktivity)

const schemeTransformer = inject<Ref<ZoppmSchemeTransformer>>('schemeTransformer')
const sekciaRefNazov: NazvyAssemblySekcii = 'zoppm.ss.aktivity'
const heading = computed(
  () =>
    schemeTransformer?.value.getSectionTitle(sekciaRefNazov) ||
    t('zoppm.zhrnutie.aktivity.mainHeading')
)
</script>

<template>
  <IDSKHeading as="h2" variant="l" class="mb-7">
    {{ heading }}
  </IDSKHeading>

  <ZhrnutieError v-if="isValidSection" />

  <template v-for="(aktivita, key) in aktivityData" :key="key">
    <IDSKHeading as="h3" variant="s" class="mb-6">
      {{ key + 1 }}. {{ aktivita.nazov }}
    </IDSKHeading>
    <DisplayList
      variant="inline"
      inline-layout="one-half"
      class="govuk-!-width-two-thirds mb-7"
    >
      <dt v-if="aktivita.intervalRealizacie?.datumZaciatok" class="mb-6">
        {{ t('zoppm.zhrnutie.aktivity.startActivity') }}
      </dt>
      <dd
        v-if="aktivita.intervalRealizacie?.datumZaciatok"
        class="d-flex align-items-right"
      >
        {{ formatDateString(aktivita.intervalRealizacie?.datumZaciatok) }}
      </dd>
      <dt v-if="aktivita.intervalRealizacie?.datumKoniec">
        {{ t('zoppm.zhrnutie.aktivity.endActivity') }}
      </dt>
      <dd
        v-if="aktivita.intervalRealizacie?.datumKoniec"
        class="d-flex align-items-right"
      >
        {{ formatDateString(aktivita.intervalRealizacie?.datumKoniec) }}
      </dd>
    </DisplayList>
  </template>

  <div v-if="!stavPodana" class="d-flex align-items-right">
    <IDSKButton
      variant="secondary"
      type="button"
      class="mb-0"
      :data-test-id="`zoppm-aktivity-projektu-edit`"
      @click="
        () =>
          steps.setStep(NazvyKrokov['aktivityProjektu'], {
            ref: NazvyKrokov['zhrnutie'],
          })
      "
    >
      {{ t('zoppm.zhrnutie.editBtn') }}
    </IDSKButton>
  </div>

  <hr class="mb-8 mt-8" />
</template>
